/**
 * The organization's Rules in its settings (`policy.rules`, `org/rules-policy.ts`
 * in contracts; §5.27, Claude Design R5), under Git & merge: whether a session
 * waits for a Rules audit before it is merged. The rules themselves are each
 * workspace's, in its rules repository.
 *
 * Policy, like the pull-request rules: nothing on a member's machine inherits
 * or overrides it. Published with the rest of the settings, as the « Rules »
 * line of the diff.
 */
import type React from 'react';
import { EMPTY_ORG_POLICY, policyOf, type OrgPolicy } from '@airlock/contracts/org/policy';
import type { OrgRules } from '@airlock/contracts/org/rules-policy';
import { SettingToggle } from '../../components/settings-row';
import { color } from '../../theme/tokens';
import type { OrgSettings } from '../../lib/types';
import { ListBlock } from './list-settings';

export function RulesPolicy({
  settings,
  editable,
  onChange
}: {
  settings: OrgSettings;
  editable: boolean;
  onChange: (next: OrgPolicy) => void;
}): React.JSX.Element {
  const policy = policyOf(settings);
  const rules: OrgRules = policy.rules;
  const put = (patch: Partial<OrgRules>): void => onChange({ ...EMPTY_ORG_POLICY, ...policy, rules: { ...rules, ...patch } });

  return (
    <ListBlock
      label="Require an audit before every merge"
      hint="A session in any workspace of the organization is not merged until a Rules audit of its change has run, and a broken MUST of an enforced rule holds it. The rules are each workspace’s, in its rules repository."
    >
      {editable ? (
        <SettingToggle on={rules.requireAudit} onToggle={() => put({ requireAudit: !rules.requireAudit })} />
      ) : (
        <p style={{ margin: 0, fontSize: 13, color: color.inkMuted }}>{rules.requireAudit ? 'Yes' : 'No'}</p>
      )}
    </ListBlock>
  );
}
